import { motion } from "framer-motion";
import {
  Layers, Cpu, Brain, Radio, Code2, CircuitBoard, Zap, ClipboardList, ArrowUpRight,
} from "lucide-react";

/** Bento grid: 12 cols on lg. Mixed sizes for varied rhythm. */
const expertise = [
  {
    icon: Layers,
    title: "Product Management",
    desc: "Menyusun roadmap, MVP, prioritas fitur, kebutuhan stakeholder, dan strategi pengembangan produk digital.",
    tag: "01 — Strategy",
    span: "lg:col-span-5 lg:row-span-2",
    accent: "from-blue-500/20 to-indigo-500/10",
    iconColor: "text-blue-300",
  },
  {
    icon: Cpu,
    title: "Technical Solution",
    desc: "Merancang solusi teknis untuk backend, API, integrasi sistem, cloud, IoT, dashboard, dan deployment.",
    tag: "02 — Architecture",
    span: "lg:col-span-7",
    accent: "from-cyan-500/15 to-blue-500/10",
    iconColor: "text-cyan-300",
  },
  {
    icon: Brain,
    title: "Artificial Intelligence",
    desc: "AI solution, chatbot, automation, data analytics, report intelligence, dan workflow berbasis AI.",
    tag: "03 — Intelligence",
    span: "lg:col-span-4",
    accent: "from-violet-500/20 to-indigo-500/10",
    iconColor: "text-violet-300",
  },
  {
    icon: Radio,
    title: "IoT Monitoring",
    desc: "MQTT, telemetry, sensor, device monitoring, real-time data, dan integrasi perangkat hardware.",
    tag: "04 — Connectivity",
    span: "lg:col-span-3",
    accent: "from-sky-500/15 to-cyan-500/10",
    iconColor: "text-sky-300",
  },
  {
    icon: Code2,
    title: "Software Development",
    desc: "Web app, mobile app, backend API, microservices, dashboard monitoring, dan sistem enterprise.",
    tag: "05 — Build",
    span: "lg:col-span-5",
    accent: "from-indigo-500/15 to-blue-500/10",
    iconColor: "text-indigo-300",
  },
  {
    icon: CircuitBoard,
    title: "Hardware Prototype",
    desc: "Prototype hardware, embedded system, sensor integration, dan proof of concept untuk kebutuhan produk.",
    tag: "06 — Prototype",
    span: "lg:col-span-4",
    accent: "from-blue-500/15 to-sky-500/10",
    iconColor: "text-blue-300",
  },
  {
    icon: Zap,
    title: "Smart Energy",
    desc: "Monitoring listrik, smart meter, EV charging, energy dashboard, dan platform energi digital.",
    tag: "07 — Energy",
    span: "lg:col-span-5",
    accent: "from-amber-500/10 to-violet-500/10",
    iconColor: "text-amber-200",
  },
  {
    icon: ClipboardList,
    title: "Project Management",
    desc: "Koordinasi tim, timeline, komunikasi stakeholder, delivery project, dan improvement produk.",
    tag: "08 — Delivery",
    span: "lg:col-span-3",
    accent: "from-emerald-500/10 to-cyan-500/10",
    iconColor: "text-emerald-300",
  },
];

const Expertise = () => {
  return (
    <section
      id="expertise"
      data-testid="expertise-section"
      className="relative py-24 lg:py-32 bg-section-tint"
    >
      <div className="absolute inset-0 grid-bg-fine opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-blue-400 font-mono-display">
              <span className="w-6 h-px bg-blue-400" />
              /// Expertise
            </div>
            <h2
              className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white"
              style={{ fontFamily: "Outfit" }}
              data-testid="expertise-title"
            >
              Fokus portfolio dan{" "}
              <span className="text-gradient-aurora">layanan teknologi.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-2">
            <p className="text-base text-slate-400 leading-relaxed">
              Keahlian meliputi AI, IoT, software development, hardware integration, smart energy,
              digital platform, dan manajemen project teknologi — disusun dari pengalaman langsung
              membangun produk yang dipakai jutaan pengguna.
            </p>
          </div>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 lg:grid-rows-[auto_auto_auto] gap-5 auto-rows-fr">
          {expertise.map(({ icon: Icon, title, desc, tag, span, accent, iconColor }, idx) => (
            <motion.div
              key={title}
              data-testid={`expertise-card-${idx}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: (idx % 4) * 0.06 }}
              className={`group relative ${span} rounded-2xl p-6 sm:p-7 glass-elevated corners scanline-hover glow-border hover:-translate-y-1 transition-transform duration-300 overflow-hidden`}
            >
              {/* gradient accent */}
              <div className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />

              <div className="relative flex items-start justify-between mb-5">
                <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/10 grid place-items-center group-hover:border-blue-400/40 group-hover:shadow-[0_0_24px_rgba(59,130,246,0.3)] transition-all">
                  <Icon className={`w-5 h-5 ${iconColor}`} strokeWidth={1.5} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono-display">
                  {tag}
                </span>
              </div>
              <h3
                className="relative text-lg sm:text-xl text-white font-medium"
                style={{ fontFamily: "Outfit" }}
              >
                {title}
              </h3>
              <p className="relative mt-2.5 text-sm text-slate-400 leading-relaxed">{desc}</p>

              <div className="relative mt-5 flex items-center gap-2 text-xs text-blue-300/80 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all">
                <span>Explore</span>
                <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.75} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Expertise;
